"use client";

import { motion } from "framer-motion";
import ShadowCard from "@/components/common/ShadowCard";
import { whyItMattersData, WhyItMattersItem } from "@/data/whyItMattersData";

interface CardItemProps {
  item: WhyItMattersItem;
  index: number;
}

function CardItem({ item, index }: CardItemProps) {
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.6,
        delay: index * 0.15,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className="h-full"
    >
      <ShadowCard className="h-full p-[18px]">
        <div className="flex flex-col gap-2.5">
          {/* Icon */}
          <Icon className="h-[23px] w-[23px] text-white md:h-[37px] md:w-[37px]" />

          {/* Title */}
          <h3 className="font-raleway text-xl leading-tight font-bold text-white">
            {item.title}
          </h3>

          {/* Description */}
          <p className="font-raleway text-base leading-[1.4] text-white/80">
            {item.description}
          </p>
        </div>
      </ShadowCard>
    </motion.div>
  );
}

export default function WhyItMattersSection() {
  return (
    <section className="bg-brand-black w-full px-5 py-[100px] md:px-[100px] md:py-24">
      <div>
        {/* Header Section */}
        <motion.div
          className="mb-5 text-center md:mb-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="font-raleway mb-2.5 text-2xl font-bold text-white md:mb-5 md:text-[32px]">
            Why It Matters
          </h2>
          <p className="font-raleway mx-auto max-w-5xl text-base leading-[1.4] text-gray-300">
            This program began as a pilot in 2021 and has since been formalized
            into law, providing clear guidelines on licensing, redemption, and
            prize limitations. It also supports the use of self-service
            redemption kiosks, giving operators more tools to streamline prize
            distribution.
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="mt-5 grid grid-cols-1 gap-5 md:mt-10 md:grid-cols-2 md:gap-5">
          {whyItMattersData.map((item, index) => (
            <CardItem key={item.title} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
